import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { DIRECTED_WIZARD_STEPS, directedStepIndex, type DirectedWizardStep } from '../directedSteps';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { directedMessages as messages } from '../messages/directedMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

export const DIRECTED_STEP_NAMES: Readonly<Record<DirectedWizardStep, MessageDescriptor>> = {
  cast: messages.stepCast,
  stories: messages.stepStory,
  style: messages.stepStyle,
  script: messages.stepScript,
  price: messages.stepPrice,
  progress: messages.stepMake,
};

/** The last step that the artist can open again: the price (the make cannot be opened again). */
const LAST_OPEN_INDEX = DIRECTED_WIZARD_STEPS.indexOf('price');

/**
 * The six-step stepper of the top bar (DvTop): Cast, Story, Style, Script, Price, Make. A done step
 * shows a tick, the step on view is lit. While the video is still edited, a done step is a button
 * that opens it again (`?step=`, so Back returns); the current step is a button that comes back.
 * After the make started, each earlier step opens its part of "How it was made" (design
 * directed-video l1 note, `?view=made&step=`), and Make comes back to the make or the finished video.
 */
const DirectedStepperComponent: React.FC = () => {
  const intl = useIntl();
  const { screen, currentScreen, openStep, isMadeView, madeStep, openMade } = useDirectedVideoContext();
  const currentIndex = directedStepIndex(currentScreen);
  const isMade = currentScreen === 'progress';
  let shownIndex = directedStepIndex(screen);
  if (isMadeView) {
    shownIndex = madeStep === null ? -1 : directedStepIndex(madeStep);
  }
  const canRevisit = currentIndex <= LAST_OPEN_INDEX || isMade;

  return (
    <ol className={styles.steps} aria-label={intl.formatMessage(messages.stepsLabel)}>
      {DIRECTED_WIZARD_STEPS.map((step, index) => {
        const isDone = index < currentIndex && index !== shownIndex;
        const isNow = index === shownIndex;
        const name = intl.formatMessage(DIRECTED_STEP_NAMES[step]);
        const content = (
          <>
            <i aria-hidden>{isDone ? '✓' : String(index + 1)}</i>
            {name}
          </>
        );
        const className = clsx(styles.step, isDone && styles.stepDone, isNow && styles.stepNow);
        const opens = canRevisit && index <= currentIndex && !isNow;
        const open = () => {
          if (index === currentIndex) {
            openStep(null);
          } else if (isMade) {
            openMade(step);
          } else {
            openStep(step);
          }
        };
        return (
          <li key={step} className={styles.stepItem} aria-current={isNow ? 'step' : undefined}>
            {index > 0 && <span className={styles.stepLine} aria-hidden />}
            {opens ? (
              <button
                type="button"
                className={className}
                title={intl.formatMessage(messages.stepOpen, { step: name })}
                onClick={open}
              >
                {content}
              </button>
            ) : (
              <span className={className}>{content}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export const DirectedStepper = TypedMemo(DirectedStepperComponent);
